import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check, X as XIcon, Copy, CheckCheck, ChevronDown,
  Shield, Zap, Globe2, Code2, Layers, Database, Twitter,
  ShoppingCart, Github, Lock, Server, Activity, BarChart2,
  ArrowRight, Cpu, Sun, Moon, Menu,
} from "lucide-react";
import { useSiteSettings } from "../hooks/useSiteSettings";
import { useTheme } from "../context/ThemeContext";
import { api } from "../lib/api";

// ─── Design tokens ────────────────────────────────────────────────────────────
const BG0 = "var(--c-bg)";
const BG1 = "var(--c-surface)";
const ACC = "#00ff88";

// ─── Static data ──────────────────────────────────────────────────────────────
const STATS_BAR = [
  { value: "2.4M+", label: "requests / month" },
  { value: "50ms",  label: "avg response time" },
  { value: "99.2%", label: "uptime SLA" },
  { value: "13",    label: "bypass tiers" },
];

const PLANS_FALLBACK = [
  {
    slug: "free", name: "Free", is_free: true, badge: null,
    pricing: { monthly_usd: 0, monthly_inr: 0, annual_usd: 0, annual_inr: 0 },
    limits: { credits: 1000, team_access: false, max_workspaces: 0, max_members: 0, max_groups: 0 },
    features: [
      { text: "1,000 credits / mo", included: true },
      { text: "100 req / day", included: true },
      { text: "JS render", included: true },
      { text: "1 API key", included: true },
      { text: "Stealth mode", included: false },
      { text: "Team access", included: false },
    ],
  },
  {
    slug: "starter", name: "Starter", is_free: false, badge: null,
    pricing: { monthly_usd: 19, monthly_inr: 1577, annual_usd: 190, annual_inr: 15770 },
    limits: { credits: 50000, team_access: false, max_workspaces: 0, max_members: 0, max_groups: 0 },
    features: [
      { text: "50,000 credits / mo", included: true },
      { text: "500 req / day", included: true },
      { text: "Stealth render", included: true },
      { text: "2 API keys", included: true },
      { text: "Premium bypass", included: false },
      { text: "Team access", included: false },
    ],
  },
  {
    slug: "growth", name: "Growth", is_free: false, badge: "Popular",
    pricing: { monthly_usd: 49, monthly_inr: 4067, annual_usd: 490, annual_inr: 40670 },
    limits: { credits: 200000, team_access: true, max_workspaces: 1, max_members: 5, max_groups: 5 },
    features: [
      { text: "200,000 credits / mo", included: true },
      { text: "5,000 req / day", included: true },
      { text: "CF / Kasada bypass", included: true },
      { text: "5 API keys", included: true },
      { text: "Team management", included: true },
    ],
  },
  {
    slug: "pro", name: "Pro", is_free: false, badge: null,
    pricing: { monthly_usd: 99, monthly_inr: 8217, annual_usd: 990, annual_inr: 82170 },
    limits: { credits: 600000, team_access: true, max_workspaces: 5, max_members: 15, max_groups: 10 },
    features: [
      { text: "600,000 credits / mo", included: true },
      { text: "20,000 req / day", included: true },
      { text: "GraphQL API", included: true },
      { text: "10 API keys", included: true },
      { text: "5 workspaces · 15 members", included: true },
    ],
  },
  {
    slug: "agency", name: "Agency", is_free: false, badge: "Enterprise",
    pricing: { monthly_usd: 249, monthly_inr: 20667, annual_usd: 2490, annual_inr: 206670 },
    limits: { credits: 2000000, team_access: true, max_workspaces: 9999, max_members: 9999, max_groups: 9999 },
    features: [
      { text: "2M credits / mo", included: true },
      { text: "Unlimited req / day", included: true },
      { text: "White-label", included: true },
      { text: "99.5% SLA", included: true },
      { text: "Unlimited workspaces", included: true },
    ],
  },
];

const BENCHMARK = [
  { site: "X.com (Kasada)",    sc: true,  bee: false, zyte: null  },
  { site: "Cloudflare WAF",    sc: true,  bee: null,  zyte: true  },
  { site: "Akamai Bot Mgr",    sc: true,  bee: false, zyte: null  },
  { site: "Amazon",            sc: true,  bee: true,  zyte: true  },
  { site: "Flipkart",          sc: true,  bee: null,  zyte: null  },
  { site: "Nordstrom",         sc: true,  bee: false, zyte: false },
  { site: "LinkedIn",          sc: true,  bee: false, zyte: null  },
  { site: "Instagram",         sc: true,  bee: null,  zyte: false },
];

const TIERS = [
  { id: "T1", label: "direct_curl",         type: "http",    desc: "Raw HTTP/2, zero overhead" },
  { id: "T2", label: "curl_cffi TLS spoof", type: "http",    desc: "Browser-grade TLS fingerprint" },
  { id: "T3", label: "httpx stealth",       type: "http",    desc: "Custom headers + UA rotation" },
  { id: "T4", label: "playwright headless", type: "http",    desc: "Chromium without evasion" },
  { id: "T5", label: "rotating proxy pool", type: "http",    desc: "Datacenter IPs + rotation" },
  { id: "T6", label: "residential IP",      type: "http",    desc: "ISP-grade residential addresses" },
  { id: "T7", label: "residential + solve", type: "http",    desc: "2captcha CAPTCHA solve" },
  { id: "R1", label: "camoufox browser",    type: "browser", desc: "Anti-fingerprint Firefox engine" },
  { id: "R2", label: "fingerprint evasion", type: "browser", desc: "canvas / WebGL / audio spoof" },
  { id: "R3", label: "residential render",  type: "browser", desc: "Real ISP IP + full render" },
  { id: "R4", label: "premium bypass",      type: "browser", desc: "Cloudflare v3 / Turnstile" },
  { id: "R5", label: "Kasada defeat",       type: "browser", desc: "Sensor data + motion spoof" },
  { id: "R6", label: "Akamai defeat",       type: "browser", desc: "Bot Manager fingerprint bypass" },
];

const PYEX = `import requests

r = requests.post(
    "https://api.scrapecore.com/scrape",
    headers={"Authorization": "Bearer sk_live_xxxx"},
    json={"url": "https://x.com/elonmusk", "js_render": True},
).json()

print(r["user"]["followers_count"])  # 238.2M
print(r["user"]["name"])              # Elon Musk`;

const NDEX = `const axios = require("axios");

const { data } = await axios.post(
  "https://api.scrapecore.com/scrape",
  { url: "https://x.com/elonmusk", js_render: true },
  { headers: { Authorization: "Bearer sk_live_xxxx" } }
);

console.log(data.user.followers_count); // 238.2M`;

const CURLEX = `curl -X POST https://api.scrapecore.com/scrape \\
  -H "Authorization: Bearer sk_live_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://x.com/elonmusk", "js_render": true}'`;

const GOEX = `req, _ := http.NewRequest("POST",
  "https://api.scrapecore.com/scrape",
  strings.NewReader(body),
)
req.Header.Set("Authorization", "Bearer sk_live_xxxx")
req.Header.Set("Content-Type", "application/json")
resp, _ := http.DefaultClient.Do(req)`;

const CODE_EXAMPLES = { python: PYEX, node: NDEX, curl: CURLEX, go: GOEX };

const TERMINAL_SEQUENCE = [
  { text: "$ scrapecore run --url https://x.com/elonmusk", color: ACC },
  { text: "-> [T1] curl_direct           x  403 Forbidden",      color: "#f87171" },
  { text: "-> [T2] curl_cffi TLS         x  403 Bot detected",   color: "#f87171" },
  { text: "-> [R1] camoufox JS engine    v  200 OK   1.24s",     color: ACC },
  { text: "-> parsing profile...",                                 color: "var(--c-text-2)" },
  { text: '{ "name": "Elon Musk", "followers": 238200000 }',     color: "#93c5fd" },
  { text: "Done  credits: 8  time: 1240ms",                       color: ACC },
];

const USE_CASES = [
  { icon: Twitter,      title: "Social Intelligence",  desc: "Pull Twitter/X profiles, tweets, follower counts as structured JSON." },
  { icon: ShoppingCart, title: "Price Monitoring",      desc: "Real-time pricing from Amazon, Flipkart, Nordstrom." },
  { icon: Database,     title: "Lead Generation",       desc: "LinkedIn profiles, job postings — ready for your CRM." },
  { icon: Activity,     title: "News & Research",       desc: "Track breaking news with millisecond freshness." },
  { icon: Globe2,       title: "SEO & Auditing",        desc: "Crawl competitor pages and JS-rendered SPAs." },
  { icon: BarChart2,    title: "Market Research",       desc: "Aggregate product reviews at scale." },
];

const FAQ_ITEMS = [
  {
    q: "What is a credit and how are they calculated?",
    a: "Each API request consumes credits based on the features used. A standard HTTP scrape costs 10 credits. Enabling JavaScript rendering adds +5 credits (total 15). Adding proxy rotation adds +5 credits (total 15). Enabling WAF/Cloudflare bypass adds +5 credits. You can stack features: JS render + proxy = 20 credits; JS + proxy + WAF = 25 credits. Screenshots cost 25 credits. Structured extraction costs 10 credits. Twitter scraping costs 20 credits. Credits reset monthly on your billing cycle."
  },
  {
    q: "How does the 13-tier cascade work?",
    a: "Our engine tries the cheapest viable method first (T1 direct curl) and escalates through 7 HTTP tiers, then 6 render tiers, until one succeeds. You are charged only for the final successful tier. This maximises success rates while minimising credit usage on well-crawled sites."
  },
  {
    q: "Do you support real-time Twitter/X.com data?",
    a: "Yes. Our R-tier browser engine bypasses Twitter/X.com's Kasada protection. You can scrape profiles and recent tweets as clean JSON. Each Twitter request costs 20 credits regardless of JS render settings."
  },
  {
    q: "Is there a free trial?",
    a: "The Free plan includes 1,000 credits per month with no credit card required. You can test all endpoint types including JS rendering, extraction, and screenshots before upgrading. API rate limits and feature access vary by plan."
  },
  {
    q: "What happens when I run out of credits?",
    a: "API calls return a 402 error with a clear message. Your account is never auto-charged beyond your plan. You can upgrade your plan at any time from the Billing page, or wait for your credits to reset on your monthly billing date."
  },
  {
    q: "What is the difference between plans?",
    a: "Plans differ by monthly credit quota, rate limits (requests per minute / per day), concurrent threads, number of API keys, and access to premium features like JS rendering, stealth mode, and team workspaces. The Free plan is always available. Paid plans (Starter, Growth, Pro, Agency) are available monthly or annually."
  },
  {
    q: "Can I get a refund if I change my mind?",
    a: "Yes, within 48 hours of purchase if you have consumed less than 5% of your quota, have no TOS violations, and have cancelled your subscription first. Refunds are reviewed manually and typically processed within 5–10 business days. See our full Cancellation & Refund Policy for details."
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function TerminalWindow() {
  const [lines, setLines] = useState([]);
  const timerRef = useRef(null);

  useEffect(() => {
    let idx = 0;
    const advance = () => {
      if (idx >= TERMINAL_SEQUENCE.length) {
        timerRef.current = setTimeout(() => {
          setLines([]);
          idx = 0;
          timerRef.current = setTimeout(advance, 600);
        }, 3500);
        return;
      }
      setLines((prev) => [...prev, TERMINAL_SEQUENCE[idx]]);
      idx++;
      timerRef.current = setTimeout(advance, idx <= 3 ? 420 : 220);
    };
    timerRef.current = setTimeout(advance, 600);
    return () => clearTimeout(timerRef.current);
  }, []);

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: "var(--c-surface-2)", border: "1px solid rgba(0,255,136,0.35)" }}>
      <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/10">
        <span className="w-3 h-3 rounded-full bg-[#ff5f56]" />
        <span className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
        <span className="w-3 h-3 rounded-full bg-[#27c93f]" />
        <span className="ml-3 font-mono text-xs text-white/30">scrapecore ~ %</span>
      </div>
      <div className="p-5 min-h-[280px] font-mono text-sm leading-6">
        {lines.filter(Boolean).map((line, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.12 }}
            style={{ color: line.color }}
          >
            {line.text}
          </motion.div>
        ))}
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.8, repeat: Infinity, repeatType: "reverse" }}
          className="inline-block w-2 h-4 rounded-sm"
          style={{ background: ACC, verticalAlign: "middle" }}
        />
      </div>
    </div>
  );
}

function TierRow({ tier, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -16 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ delay: index * 0.055 }}
      className="flex items-center gap-4 py-3 border-b border-white/[0.06] last:border-0"
    >
      <div
        className={`w-11 shrink-0 text-center font-mono text-xs py-1 rounded ${
          tier.type === "browser"
            ? "bg-emerald-500/10 border border-emerald-500/25"
            : "bg-white/[0.05] text-white/50 border border-white/[0.08]"
        }`}
        style={{ color: tier.type === "browser" ? ACC : undefined }}
      >
        {tier.id}
      </div>
      <div className="flex-1 min-w-0">
        <span className="font-mono text-sm text-white/80">{tier.label}</span>
        <span className="hidden sm:inline text-white/25 text-xs ml-3">{tier.desc}</span>
      </div>
      {tier.type === "browser"
        ? <Lock size={13} style={{ color: ACC }} className="shrink-0" />
        : <Server size={13} className="shrink-0 text-white/20" />}
    </motion.div>
  );
}

function CascadeViz() {
  const httpTiers  = TIERS.filter((t) => t.type === "http");
  const rndrTiers  = TIERS.filter((t) => t.type === "browser");

  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="rounded-xl border border-white/10 p-6" style={{ background: BG1 }}>
        <p className="font-mono text-xs tracking-widest uppercase text-white/30 mb-4">HTTP Tiers — lightweight</p>
        {httpTiers.map((t, i) => <TierRow key={t.id} tier={t} index={i} />)}
      </div>
      <div className="rounded-xl border p-6" style={{ background: BG1, borderColor: "rgba(0,255,136,0.18)" }}>
        <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: ACC }}>Browser Tiers — heavy bypass</p>
        {rndrTiers.map((t, i) => <TierRow key={t.id} tier={t} index={i + 7} />)}
      </div>
    </div>
  );
}

function CodeTabs() {
  const [tab, setTab]       = useState("python");
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(CODE_EXAMPLES[tab]);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: "var(--c-bg)", border: "1px solid var(--c-border)" }}>
      <div className="flex items-center justify-between border-b border-white/10 px-1">
        <div className="flex">
          {["python", "node", "curl", "go"].map((t) => (
            <button
              key={t} onClick={() => setTab(t)}
              data-testid={`code-tab-${t}`}
              className={`px-4 py-3 font-mono text-xs transition-colors ${tab !== t ? "text-white/35" : ""}`}
              style={{
                color:        tab === t ? ACC : undefined,
                borderBottom: tab === t ? `2px solid ${ACC}` : "2px solid transparent",
              }}
            >
              {t}
            </button>
          ))}
        </div>
        <button
          onClick={copy}
          data-testid="copy-code-btn"
          className={`flex items-center gap-1.5 mr-4 px-3 py-1.5 rounded font-mono text-xs transition-all bg-white/[0.04] ${!copied ? "text-white/35" : ""}`}
          style={{ color: copied ? ACC : undefined }}
        >
          {copied ? <CheckCheck size={12} /> : <Copy size={12} />}
          {copied ? "copied" : "copy"}
        </button>
      </div>
      <pre className="p-6 overflow-x-auto font-mono text-sm leading-relaxed text-white/75 whitespace-pre">
        {CODE_EXAMPLES[tab]}
      </pre>
    </div>
  );
}

function FAQItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-item-${index}`}
        className="w-full flex items-center justify-between py-5 text-left gap-4"
      >
        <span className="text-white/85 text-base">{item.q}</span>
        <ChevronDown
          size={16} className="shrink-0 text-white/35 transition-transform duration-200"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0)" }}
        />
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
          className="pb-5 text-sm text-white/55 leading-relaxed"
        >
          {item.a}
        </motion.div>
      )}
    </div>
  );
}

function BenchCell({ v }) {
  if (v === true)  return <span style={{ color: ACC }} className="font-mono">v</span>;
  if (v === false) return <span className="text-red-400 font-mono">x</span>;
  return <span className="text-white/30 font-mono">~</span>;
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Landing() {
  const [billing,  setBilling]  = useState("monthly");
  const [currency, setCurrency] = useState("usd");
  const { settings } = useSiteSettings();
  const { theme, toggleTheme } = useTheme();
  const [plans, setPlans] = useState(PLANS_FALLBACK);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Fetch plans from API (dynamic)
  useEffect(() => {
    api.get("/plans/public").then(r => {
      if (r.data && r.data.length > 0) setPlans(r.data);
    }).catch(() => {/* fallback stays */});
  }, []);

  // Changelog: fetch from /api/changelog so admin only needs to manage one place
  const [changelog, setChangelog] = useState([]);
  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${BACKEND_URL}/api/changelog`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length) {
          // Map API format {version, release_date, changes:[{type, text}]}
          // → landing format {version, date, tag, text}
          // Show first change from each of the 5 most recent entries
          const mapped = data.slice(0, 5).flatMap(entry =>
            (entry.changes || []).slice(0, 1).map(ch => ({
              version: entry.version,
              date: entry.release_date,
              tag: ch.type === "improved" ? "improvement" : ch.type,
              text: ch.text,
            }))
          );
          setChangelog(mapped);
        }
      })
      .catch(() => {/* stay empty — section hidden when no entries */});
  }, []);

  // Core team: fetch public team members
  const [coreTeam, setCoreTeam] = useState([]);
  useEffect(() => {
    api.get("/public/team")
      .then(r => setCoreTeam(r.data?.items || []))
      .catch(() => {/* stay empty */});
  }, []);

  // Dynamic values from settings (fallback to hardcoded)
  const heroData   = settings?.hero  || {};
  const navLinks   = settings?.nav_links || [];
  const faqItems   = settings?.faq?.length ? settings.faq : FAQ_ITEMS;
  const statsBar   = settings?.stats_bar || STATS_BAR;
  const footerData = settings?.footer || {};
  const finalCta   = settings?.final_cta || {};
  const accentColor = settings?.accent_color || "#00ff88";
  const testimonials = settings?.testimonials || [];

  const getPrice = (plan) => {
    const p = plan.pricing || {};
    if (currency === "usd") {
      const val = billing === "annual" ? (p.annual_usd || Math.round((p.monthly_usd || 0) * 10)) : (p.monthly_usd || 0);
      return { symbol: "$", val, period: billing === "annual" ? "/yr" : "/mo" };
    }
    const val = billing === "annual" ? (p.annual_inr || Math.round((p.monthly_inr || 0) * 10)) : (p.monthly_inr || 0);
    return { symbol: "\u20b9", val, period: billing === "annual" ? "/yr" : "/mo" };
  };

  const fmtCredits = (plan) => {
    const c = plan.limits?.credits || 0;
    if (c >= 1e6) return `${(c / 1e6).toFixed(c % 1e6 === 0 ? 0 : 1)}M`;
    if (c >= 1e3) return `${(c / 1e3).toFixed(c % 1e3 === 0 ? 0 : 1)}K`;
    return String(c);
  };

  const isPopular = (plan) => !!plan.badge;

  const getBadgeStyle = (plan) => {
    const b = (plan.badge || "").toLowerCase();
    if (b === "popular") return { background: ACC, color: "#000", label: "most popular" };
    if (b === "recommended") return { background: "#f59e0b", color: "#000", label: "recommended" };
    if (b === "enterprise") return { background: "rgba(148,163,184,0.12)", color: "#94a3b8", label: "enterprise", border: "1px solid rgba(148,163,184,0.3)" };
    if (b === "famous") return { background: "#ef4444", color: "#fff", label: "famous" };
    return { background: ACC, color: "#000", label: b };
  };

  const getPlanCta = (plan) => plan.is_free || (plan.pricing?.monthly_usd || 0) === 0 ? "start free" : "choose plan";

  return (
    <div className="min-h-screen text-white" style={{ background: BG0 }}>

      {/* Navbar */}
      <nav
        className="sticky top-0 z-50 border-b border-white/10 glass-nav"
        style={{ backdropFilter: "blur(20px)" }}
      >
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/scrapecore-dark.png" alt="ScrapeCore" className="h-7 w-auto" />
          </Link>
          <div className="hidden md:flex gap-7 font-mono text-xs text-white/40">
            {navLinks.length > 0 ? navLinks.map((nl, i) => (
              nl.href.startsWith("#") ? (
                <a key={i} href={nl.href} className="hover:text-white transition-colors">{nl.label}</a>
              ) : (
                <Link key={i} to={nl.href} className="hover:text-white transition-colors">{nl.label}</Link>
              )
            )) : (
              <>
                <a href="#features" className="hover:text-white transition-colors">features</a>
                <a href="#cascade" className="hover:text-white transition-colors">cascade</a>
                <a href="#benchmark" className="hover:text-white transition-colors">benchmark</a>
                <a href="#pricing" className="hover:text-white transition-colors">pricing</a>
                <Link to="/docs" className="hover:text-white transition-colors">docs</Link>
              </>
            )}
            <Link to="/team" className="hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-team">
              team
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" title="New" />
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-1.5 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors"
              title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              data-testid="landing-theme-toggle"
            >
              {theme === "light" ? <Moon size={14} /> : <Sun size={14} />}
            </button>
            <Link to="/blog" className="hidden sm:block font-mono text-xs text-white/45 hover:text-white transition-colors" data-testid="nav-blog">blog</Link>
            <Link to="/login" className="hidden sm:block font-mono text-xs text-white/45 hover:text-white transition-colors" data-testid="nav-login">login</Link>
            <Link
              to="/register"
              data-testid="nav-signup"
              className="flex items-center gap-1.5 px-4 py-1.5 rounded font-mono text-xs font-semibold transition-all hover:opacity-90"
              style={{ background: ACC, color: "#000" }}
            >
              start free <ArrowRight size={12} />
            </Link>
            <button
              className="md:hidden p-1.5 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors"
              onClick={() => setMobileNavOpen(o => !o)}
              data-testid="landing-mobile-menu-btn"
            >
              {mobileNavOpen ? <XIcon size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>
        {/* Mobile nav dropdown */}
        {mobileNavOpen && (
          <div className="md:hidden border-t border-white/10 px-6 py-4 space-y-3 font-mono text-sm text-white/60" style={{ background: "var(--c-bg)" }}>
            {navLinks.length > 0 ? navLinks.map((nl, i) => (
              nl.href.startsWith("#") ? (
                <a key={i} href={nl.href} onClick={() => setMobileNavOpen(false)} className="block hover:text-white transition-colors py-1">{nl.label}</a>
              ) : (
                <Link key={i} to={nl.href} onClick={() => setMobileNavOpen(false)} className="block hover:text-white transition-colors py-1">{nl.label}</Link>
              )
            )) : (
              <>
                <a href="#features" onClick={() => setMobileNavOpen(false)} className="block hover:text-white transition-colors py-1">features</a>
                <a href="#cascade" onClick={() => setMobileNavOpen(false)} className="block hover:text-white transition-colors py-1">cascade</a>
                <a href="#benchmark" onClick={() => setMobileNavOpen(false)} className="block hover:text-white transition-colors py-1">benchmark</a>
                <a href="#pricing" onClick={() => setMobileNavOpen(false)} className="block hover:text-white transition-colors py-1">pricing</a>
                <Link to="/docs" onClick={() => setMobileNavOpen(false)} className="block hover:text-white transition-colors py-1">docs</Link>
              </>
            )}
            <Link to="/team" onClick={() => setMobileNavOpen(false)} className="block hover:text-white transition-colors py-1">team</Link>
            <Link to="/blog" onClick={() => setMobileNavOpen(false)} className="block hover:text-white transition-colors py-1">blog</Link>
            <Link to="/login" onClick={() => setMobileNavOpen(false)} className="block hover:text-white transition-colors py-1">login</Link>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid md:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
          <p className="font-mono text-xs tracking-widest uppercase mb-5" style={{ color: ACC }}>
            {heroData.badge || "// 13-tier cascade engine"}
          </p>
          <h1 className="font-head text-5xl md:text-6xl font-medium tracking-tight leading-[1.06]">
            {heroData.title || "Scrape Any Website."}<br />
            <span style={{ color: ACC }}>{heroData.title_highlight || "Beat Any Bot Wall."}</span>
          </h1>
          <p className="text-white/60 mt-6 text-lg leading-relaxed max-w-lg">
            {heroData.subtitle || "Cloudflare, Kasada, Akamai bypass. Twitter / X.com data included. Structured JSON in milliseconds."}
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link
              to={heroData.cta_primary_href || "/register"}
              data-testid="hero-cta-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded font-mono text-sm font-semibold transition-all hover:opacity-90"
              style={{ background: ACC, color: "#000" }}
            >
              {heroData.cta_primary_text || "$ start free"} <ArrowRight size={14} />
            </Link>
            <Link
              to={heroData.cta_secondary_href || "/docs"}
              data-testid="hero-docs-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded font-mono text-sm transition-all border border-white/[0.12] text-white/70 hover:text-white hover:border-white/20"
            >
              view docs
            </Link>
          </div>
          <div className="flex gap-6 mt-10 font-mono text-xs text-white/35">
            <span><span style={{ color: ACC }}>99.2%</span> uptime</span>
            <span><span style={{ color: ACC }}>50ms</span> avg response</span>
            <span><span style={{ color: ACC }}>13</span> bypass tiers</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.15 }}>
          <TerminalWindow />
        </motion.div>
      </section>

      {/* Stats bar */}
      <section className="border-y border-white/10" data-testid="stats-bar">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4">
          {statsBar.map((s, i) => (
            <div
              key={s.label}
              className={`py-8 px-6 ${i < statsBar.length - 1 ? "md:border-r border-white/10" : ""}`}
            >
              <div className="font-mono text-3xl md:text-4xl font-semibold" style={{ color: ACC }}>{s.value}</div>
              <div className="font-mono text-xs uppercase tracking-wider text-white/30 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-28">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: ACC }}>// capabilities</p>
          <h2 className="font-head text-4xl font-medium tracking-tight">Built for the wall.</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.05 }}
            className="md:col-span-7 rounded-xl border border-white/10 p-8" style={{ background: BG1 }}
          >
            <Shield size={28} style={{ color: ACC }} />
            <h3 className="font-head text-2xl font-medium mt-5">Cloudflare + JS Challenge bypass</h3>
            <p className="text-white/55 mt-3 leading-relaxed max-w-lg">
              curl_cffi TLS spoofing + camoufox anti-fingerprint Firefox.
              When their JS challenge fires, our R1 tier responds with a real browser fingerprint.
            </p>
            <div className="flex gap-2 mt-6 flex-wrap">
              {["tier R1", "tier R2", "camoufox", "TLS spoof"].map((b) => (
                <span key={b} className="font-mono text-xs px-3 py-1 rounded" style={{ background: "rgba(0,255,136,0.08)", color: ACC, border: "1px solid rgba(0,255,136,0.2)" }}>{b}</span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 }} className="md:col-span-5 rounded-xl border border-white/10 p-8" style={{ background: BG1 }}>
            <Zap size={24} style={{ color: ACC }} />
            <h3 className="font-head text-xl font-medium mt-4">Kasada + Akamai Defeat</h3>
            <p className="text-white/50 mt-2 text-sm leading-relaxed">Residential IPs + DOM rendering for the toughest enterprise WAF layers.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="md:col-span-4 rounded-xl border border-white/10 p-8" style={{ background: BG1 }}>
            <Twitter size={22} style={{ color: ACC }} />
            <h3 className="font-head text-xl font-medium mt-4">Twitter / X.com</h3>
            <p className="text-white/50 mt-2 text-sm leading-relaxed">Profile, tweets, follower counts in clean JSON. No developer account needed.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.12 }} className="md:col-span-4 rounded-xl border border-white/10 p-8" style={{ background: BG1 }}>
            <ShoppingCart size={22} style={{ color: ACC }} />
            <h3 className="font-head text-xl font-medium mt-4">E-commerce Live Data</h3>
            <p className="text-white/50 mt-2 text-sm leading-relaxed">Amazon, Flipkart, Nordstrom — real-time pricing and inventory.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.14 }} className="md:col-span-4 rounded-xl border border-white/10 p-8" style={{ background: BG1 }}>
            <Layers size={22} style={{ color: ACC }} />
            <h3 className="font-head text-xl font-medium mt-4">13-Tier Auto Cascade</h3>
            <p className="text-white/50 mt-2 text-sm leading-relaxed">T1-T7 HTTP, R1-R6 render. We always pick the cheapest tier that works.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.16 }} className="md:col-span-6 rounded-xl border border-white/10 p-8" style={{ background: BG1 }}>
            <Code2 size={22} style={{ color: ACC }} />
            <h3 className="font-head text-xl font-medium mt-4">REST + GraphQL</h3>
            <p className="text-white/50 mt-2 text-sm leading-relaxed">Clean REST on all plans, Strawberry GraphQL on Pro+.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.18 }} className="md:col-span-6 rounded-xl border border-white/10 p-8" style={{ background: BG1 }}>
            <Cpu size={22} style={{ color: ACC }} />
            <h3 className="font-head text-xl font-medium mt-4">Async Job Queue</h3>
            <p className="text-white/50 mt-2 text-sm leading-relaxed">Fire-and-forget large crawl jobs. Webhook delivery when done.</p>
          </motion.div>
        </div>
      </section>

      {/* 13-tier cascade visualization */}
      <section id="cascade" className="max-w-7xl mx-auto px-6 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: ACC }}>// bypass_layers</p>
          <h2 className="font-head text-4xl font-medium tracking-tight mb-3">The 13 bypass layers.</h2>
          <p className="text-white/50 text-base max-w-2xl">Every request cascades from the cheapest tier upward until one succeeds. You pay per attempt.</p>
        </motion.div>
        <div className="mt-10">
          <CascadeViz />
        </div>
      </section>

      {/* Code examples */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: ACC }}>// quickstart</p>
            <h2 className="font-head text-4xl font-medium tracking-tight">Two lines of code.<br />Real Twitter data.</h2>
            <p className="text-white/55 mt-4 leading-relaxed max-w-md">Skip the proxy plumbing. Hit our endpoint, get clean structured JSON.</p>
            <div className="mt-8 flex flex-col gap-3">
              {[
                { icon: Shield, text: "No proxy setup required" },
                { icon: Zap,    text: "Sub-second response times" },
                { icon: Globe2, text: "Works on any public website" },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3 font-mono text-sm text-white/55">
                  <Icon size={14} style={{ color: ACC }} /> {text}
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <CodeTabs />
          </motion.div>
        </div>
      </section>

      {/* Benchmark */}
      <section id="benchmark" className="max-w-7xl mx-auto px-6 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: ACC }}>// benchmark</p>
          <h2 className="font-head text-4xl font-medium tracking-tight">
            We win where they <span className="text-red-400">fail</span>.
          </h2>
        </motion.div>
        <div className="mt-10 rounded-xl border border-white/10 overflow-hidden overflow-x-auto" style={{ background: BG1 }}>
          <table className="w-full font-mono text-sm min-w-[500px]">
            <thead>
              <tr className="border-b border-white/10 text-xs text-white/30 uppercase tracking-wider">
                <th className="text-left px-6 py-4">Site / Protection</th>
                <th className="text-center px-6 py-4" style={{ color: ACC }}>ScrapeCore</th>
                <th className="text-center px-6 py-4">ScrapingBee</th>
                <th className="text-center px-6 py-4">Zyte</th>
              </tr>
            </thead>
            <tbody>
              {BENCHMARK.map((row) => (
                <tr key={row.site} className="border-b border-white/[0.05] hover:bg-white/[0.02] transition-colors">
                  <td className="px-6 py-4 text-white/70">{row.site}</td>
                  <td className="px-6 py-4 text-center"><BenchCell v={row.sc} /></td>
                  <td className="px-6 py-4 text-center"><BenchCell v={row.bee} /></td>
                  <td className="px-6 py-4 text-center"><BenchCell v={row.zyte} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-6 py-4 border-t border-white/10 flex gap-6 text-xs font-mono text-white/30">
            <span><span style={{ color: ACC }}>v</span> confirmed bypass</span>
            <span><span className="text-red-400">x</span> blocked / failed</span>
            <span>~ partial / inconsistent</span>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: ACC }}>// use_cases</p>
          <h2 className="font-head text-4xl font-medium tracking-tight">What you can build.</h2>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {USE_CASES.map(({ icon: Icon, title, desc }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
              className="rounded-xl border border-white/10 p-6 hover:border-white/20 transition-colors"
              style={{ background: BG1 }}
            >
              <Icon size={20} style={{ color: ACC }} />
              <h3 className="font-head text-base font-medium mt-4 mb-2">{title}</h3>
              <p className="text-sm text-white/50 leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: ACC }}>// pricing</p>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <h2 className="font-head text-4xl font-medium tracking-tight">
              Pay for credits.<br /><span style={{ color: ACC }}>Not for failure.</span>
            </h2>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex border border-white/10 rounded font-mono text-xs overflow-hidden">
                <button onClick={() => setBilling("monthly")} data-testid="pricing-monthly" className={`px-4 py-2 transition-all ${billing === "monthly" ? "bg-accent text-black" : "text-white/40"}`}>monthly</button>
                <button onClick={() => setBilling("annual")} data-testid="pricing-annual" className={`px-4 py-2 transition-all ${billing === "annual" ? "bg-accent text-black" : "text-white/40"}`}>annual (2 mo free)</button>
              </div>
              <div className="flex border border-white/10 rounded font-mono text-xs overflow-hidden">
                <button onClick={() => setCurrency("usd")} data-testid="pricing-usd-toggle" className={`px-4 py-2 transition-all ${currency === "usd" ? "bg-white/[0.08] text-white" : "text-white/40"}`}>USD $</button>
                <button onClick={() => setCurrency("inr")} data-testid="pricing-inr-toggle" className={`px-4 py-2 transition-all ${currency === "inr" ? "bg-white/[0.08] text-white" : "text-white/40"}`}>INR</button>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {plans.map((plan, i) => {
            const p = getPrice(plan);
            const popular = isPopular(plan);
            return (
              <motion.div
                key={plan.slug || plan.key}
                data-testid={`plan-${plan.slug || plan.key}`}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                className={`relative rounded-xl flex flex-col p-6 ${!popular ? "border border-white/10" : ""}`}
                style={{
                  background: popular ? "rgba(0,255,136,0.04)" : BG1,
                  border:     popular ? `1px solid ${ACC}` : undefined,
                  boxShadow:  popular ? "0 0 32px rgba(0,255,136,0.07)" : "none",
                }}
              >
                {popular && (
                  <span className="absolute -top-3 left-4 font-mono text-xs px-3 py-0.5 rounded-full" style={getBadgeStyle(plan)}>
                    {getBadgeStyle(plan).label}
                  </span>
                )}
                <div className="font-head text-lg font-medium">{plan.name}</div>
                <div className="mt-4 mb-1">
                  <span className={`font-mono text-3xl font-semibold ${!popular ? "text-white" : ""}`} style={{ color: popular ? ACC : undefined }}>
                    {p.symbol}{p.val.toLocaleString()}
                  </span>
                  <span className="font-mono text-xs text-white/35 ml-1">{p.period}</span>
                </div>
                <div className="font-mono text-xs text-white/30 uppercase tracking-wider">{fmtCredits(plan)} credits</div>
                <ul className="mt-5 space-y-2 flex-1">
                  {(plan.features || []).map((f, fi) => {
                    const text = typeof f === "string" ? f : f.text;
                    const included = typeof f === "string" ? true : f.included;
                    return (
                      <li key={fi} className={`flex items-start gap-2 text-xs ${included ? "text-white/65" : "text-white/25"}`}>
                        {included
                          ? <Check size={12} style={{ color: ACC }} className="mt-0.5 shrink-0" />
                          : <XIcon size={12} className="mt-0.5 shrink-0 text-white/20" />}
                        {text}
                      </li>
                    );
                  })}
                  {/* Team access */}
                  {plan.limits?.team_access && plan.limits?.max_workspaces > 0 && (
                    <>
                      <li className="flex items-start gap-2 text-xs border-t border-white/[0.07] pt-2 mt-1" style={{ color: ACC }}>
                        <Check size={12} style={{ color: ACC }} className="mt-0.5 shrink-0" />Team management
                      </li>
                      <li className="flex items-start gap-2 text-xs text-white/40 pl-3">
                        <span className="text-white/20 shrink-0">·</span>
                        {plan.limits.max_workspaces >= 9999 ? "∞" : plan.limits.max_workspaces} workspace{plan.limits.max_workspaces === 1 ? "" : "s"} · {plan.limits.max_members >= 9999 ? "∞" : plan.limits.max_members} members
                      </li>
                    </>
                  )}
                </ul>
                <Link
                  to="/register"
                  data-testid={`plan-cta-${plan.slug || plan.key}`}
                  className={`mt-6 text-center rounded py-2 font-mono text-xs font-semibold transition-all hover:opacity-90 ${
                    popular
                      ? "bg-accent text-black"
                      : "bg-white/[0.06] text-white/70 border border-white/10"
                  }`}
                >
                  {getPlanCta(plan)}
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: ACC }}>// faq</p>
          <h2 className="font-head text-4xl font-medium tracking-tight mb-8">Common questions.</h2>
        </motion.div>
        {faqItems.map((item, i) => <FAQItem key={i} item={item} index={i} />)}
      </section>

      {/* Meet the Team */}
      {coreTeam.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-20" data-testid="core-team-section">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex items-end justify-between mb-12">
            <div>
              <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: ACC }}>// the_team</p>
              <h2 className="font-head text-4xl font-medium tracking-tight">The people behind it.</h2>
            </div>
            <Link to="/team" className="hidden sm:flex items-center gap-1.5 font-mono text-xs text-white/30 hover:text-white transition-colors border border-white/[0.08] hover:border-white/20 px-4 py-2 rounded-full" data-testid="view-full-team-btn">
              Full team →
            </Link>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {coreTeam.slice(0, 4).map((member, i) => {
              const initials = (member.name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
              return (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="group rounded-xl border p-6 flex flex-col gap-4 transition-all hover:border-white/15"
                  style={{ background: BG1, borderColor: "rgba(255,255,255,0.07)" }}
                  data-testid={`landing-team-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl overflow-hidden border border-white/10 flex items-center justify-center shrink-0"
                    style={{ background: "rgba(0,255,136,0.08)" }}>
                    {member.avatar_url ? (
                      <img src={member.avatar_url} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="font-mono text-base font-bold" style={{ color: ACC }}>{initials}</span>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white font-head">{member.name}</p>
                    {member.public_title && (
                      <p className="font-mono text-[11px] mt-0.5" style={{ color: ACC }}>{member.public_title}</p>
                    )}
                    {member.public_bio && (
                      <p className="text-xs text-white/40 mt-2 leading-relaxed line-clamp-2 font-body">{member.public_bio}</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
          {coreTeam.length > 4 && (
            <div className="mt-8 text-center">
              <Link to="/team" className="font-mono text-xs text-white/40 hover:text-white transition-colors border border-white/10 hover:border-white/20 px-4 py-2 rounded-full" data-testid="meet-full-team-btn">
                Meet the full team ({coreTeam.length} members) →
              </Link>
            </div>
          )}
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-20" data-testid="testimonials-section">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: ACC }}>// what_they_say</p>
            <h2 className="font-head text-4xl font-medium tracking-tight mb-12">Trusted by data teams.</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-xl border border-white/10 p-6 flex flex-col gap-4" style={{ background: BG1 }}
                data-testid={`testimonial-${i}`}
              >
                <p className="text-white/70 text-sm leading-relaxed flex-1">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-3 border-t border-white/[0.07]">
                  {t.avatar ? (
                    <img src={t.avatar} alt={t.name} className="w-8 h-8 rounded-full object-cover opacity-80" />
                  ) : (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs font-bold" style={{ background: "rgba(0,255,136,0.12)", color: ACC }}>
                      {(t.name || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-semibold text-white font-head">{t.name}</div>
                    <div className="text-xs text-white/40 font-mono">{t.role}{t.company ? ` · ${t.company}` : ""}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Changelog */}
      {changelog.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 py-16" data-testid="changelog-section">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: ACC }}>// changelog</p>
            <h2 className="font-head text-4xl font-medium tracking-tight mb-8">What's new.</h2>
          </motion.div>
          <div className="space-y-3">
            {changelog.map((item, i) => {
              const tagColors = {
                new:         { bg: "rgba(0,255,136,0.08)",  text: ACC,               border: "rgba(0,255,136,0.2)"  },
                improvement: { bg: "rgba(59,130,246,0.08)", text: "#60a5fa",          border: "rgba(59,130,246,0.2)" },
                fix:         { bg: "rgba(245,158,11,0.08)", text: "#fbbf24",          border: "rgba(245,158,11,0.2)" },
                deprecated:  { bg: "rgba(239,68,68,0.08)",  text: "#f87171",          border: "rgba(239,68,68,0.2)"  },
              };
              const tc = tagColors[item.tag] || tagColors.new;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="flex items-start gap-4 py-4 border-b border-white/[0.07] last:border-0"
                  data-testid={`changelog-item-${i}`}
                >
                  <div className="flex flex-col items-center gap-1 min-w-[56px]">
                    <span className="font-mono text-xs font-semibold" style={{ color: ACC }}>{item.version}</span>
                    <span className="font-mono text-[10px] text-white/30">{item.date}</span>
                  </div>
                  <div className="flex-1 flex items-start gap-3">
                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold capitalize mt-0.5 shrink-0" style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}` }}>
                      {item.tag}
                    </span>
                    <p className="text-sm text-white/70 leading-relaxed">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-8 text-center">
            <Link to="/changelog" className="font-mono text-xs text-white/40 hover:text-white transition-colors border border-white/10 hover:border-white/20 px-4 py-2 rounded-full" data-testid="view-all-changelog-btn">
              View full changelog →
            </Link>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="border-y border-white/10 py-28" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="font-mono text-xs tracking-widest uppercase mb-6" style={{ color: ACC }}>// ready</p>
            <h2 className="font-head text-5xl md:text-6xl font-medium tracking-tight mb-6">Ready to scrape?</h2>
            <p className="text-white/55 text-lg max-w-xl mx-auto leading-relaxed">1,000 free credits. No credit card. Start in 60 seconds.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-10">
              <Link
                to="/register"
                data-testid="cta-start-free-btn"
                className="flex items-center gap-2 px-7 py-3 rounded font-mono text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: ACC, color: "#000" }}
              >
                $ start free <ArrowRight size={14} />
              </Link>
              <Link
                to="/docs"
                className="flex items-center gap-2 px-7 py-3 rounded font-mono text-sm transition-all border border-white/[0.12] text-white/60 hover:text-white hover:border-white/20"
              >
                read the docs
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.07]" style={{ background: "var(--c-bg)" }} data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-12 grid grid-cols-2 md:grid-cols-6 gap-10">
          <div className="col-span-2 space-y-5">
            <img src="/scrapecore-dark.png" alt="ScrapeCore" className="h-8 w-auto" data-testid="footer-logo" />
            <p className="text-white/40 text-sm leading-relaxed max-w-xs">
              Enterprise web intelligence platform. 13-tier cascade engine built to beat any bot protection.
            </p>
            <a href="/status" data-testid="footer-status-link"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.03] hover:border-white/[0.15] transition-colors text-xs text-white/40 hover:text-white">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              All systems operational
            </a>
          </div>

          {(footerData.link_groups || []).map((group, gi) => (
            <div key={gi} className="space-y-4">
              <p className="text-xs font-semibold text-white tracking-widest uppercase">{group.heading}</p>
              <ul className="space-y-3">
                {(group.links || []).map(({ label, href }, li) => (
                  <li key={li}>
                    {href?.startsWith("http") || href?.startsWith("//") ? (
                      <a href={href} target="_blank" rel="noreferrer" className="text-sm text-white/35 hover:text-white transition-colors">{label}</a>
                    ) : href?.startsWith("#") ? (
                      <a href={href} className="text-sm text-white/35 hover:text-white transition-colors">{label}</a>
                    ) : (
                      <Link to={href || "/"} className="text-sm text-white/35 hover:text-white transition-colors">{label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/[0.07]">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-white/30" data-testid="footer-copyright">
              {footerData.copyright || "© 2026 ScrapeCore"}
            </p>
            <div className="flex items-center gap-5">
              {(footerData.bottom_links || []).map(({ label, href }, i) => (
                href?.startsWith("http") ? (
                  <a key={i} href={href} className="text-xs text-white/30 hover:text-white/60 transition-colors">{label}</a>
                ) : (
                  <Link key={i} to={href || "/"} className="text-xs text-white/30 hover:text-white/60 transition-colors">{label}</Link>
                )
              ))}
              {footerData.github_url && (
                <a href={footerData.github_url} target="_blank" rel="noreferrer" data-testid="footer-github-link" className="text-white/30 hover:text-white transition-colors">
                  <Github size={15} strokeWidth={1.5} />
                </a>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
